import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Circle, XCircle, FileArrowUp, LockKeyOpen, PaperPlaneTilt, Camera, Eye, CaretDown, ShareNetwork, DownloadSimple, X, PencilSimple, Trash, Phone, MapPin, Lightning, CircleNotch, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError, ROLE_LABELS, formatDateTime, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { LockTimer } from "../components/LockTimer";
import { DocThumbnail } from "../components/DocThumbnail";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { VoiceTypingButton } from "../components/VoiceTypingButton";

const DOC_LABELS = {
  sealing_certificate: "Sealing certificate",
  indent_copy: "Indent copy",
  config_screenshot: "Config screenshot",
  site_photo: "Site photo",
  other: "Other document",
};

const ORDER = ["created", "forwarded_to_manager", "with_manager", "forwarded_to_field", "with_field_officer",
  "submitted_to_discom", "indent_generated", "meter_installed", "meter_configured", "completed"];

const STEP_DOC = { indent_generated: "indent_copy", meter_installed: "sealing_certificate", meter_configured: "config_screenshot", completed: "site_photo" };
const fmtD = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
const dayGap = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));
const gapText = (n) => (n === 0 ? "same day" : `${n} day${n === 1 ? "" : "s"} later`);

const stepDates = (c) => {
  const hist = c.history || [];
  let prev = null;
  return STEPS.map((s) => {
    const h = [...hist].reverse().find((x) => x.status === s.key);
    const dt = STEP_DOC[s.key] && [...(c.documents || [])].reverse().find((d) => d.doc_type === STEP_DOC[s.key]);
    const at = h?.at || null;
    const gap = at && prev ? dayGap(prev, at) : null;
    const since = prev;
    if (at) prev = at;
    return { at, by: h?.by_name, gap, since, doc: dt ? { at: dt.uploaded_at, label: DOC_LABELS[dt.doc_type] } : null };
  });
};

const StepMeta = ({ m, active }) => (
  <div className="mt-1 space-y-0.5" data-testid="step-meta">
    {m.at && (
      <div className="text-[11px] text-slate-600 flex flex-wrap items-center gap-x-1.5">
        <span className="font-mono font-semibold">{fmtD(m.at)}</span>
        {m.by && <span className="text-slate-400">· {m.by}</span>}
        {m.gap !== null && <span className="rounded-full bg-slate-100 text-slate-600 font-semibold px-1.5 py-px text-[10px]">{gapText(m.gap)}</span>}
      </div>
    )}
    {m.doc && <div className="text-[11px] text-slate-500"><span className="text-slate-400">{m.doc.label} uploaded:</span> <span className="font-mono">{fmtD(m.doc.at)}</span></div>}
    {active && !m.at && m.since && <div className="text-[11px] font-semibold text-amber-700">Waiting {dayGap(m.since, new Date().toISOString())} day(s)</div>}
  </div>
);

const STEPS = [
  { key: "created", label: "File created", sub: "Operator" },
  { key: "with_manager", label: "File received by manager", sub: "Physical copy" },
  { key: "with_field_officer", label: "File received by field officer", sub: "Physical copy" },
  { key: "submitted_to_discom", label: "Submitted to Discom", sub: "Field officer" },
  { key: "indent_generated", label: "Indent generated", sub: "Field officer" },
  { key: "meter_installed", label: "Smart meter installed", sub: "Manager" },
  { key: "meter_configured", label: "Meter configured", sub: "Field officer" },
  { key: "completed", label: "Solar activated", sub: "Installation team" },
];

const NEXT_STEP = {
  created: "Operator hands the physical file to the manager",
  forwarded_to_manager: "Manager confirms the physical file is received",
  with_manager: "Manager hands the file to the field officer",
  forwarded_to_field: "Field officer confirms the physical file is received",
  with_field_officer: "Field officer submits the file to Discom",
  submitted_to_discom: "Field officer uploads the indent from Discom",
  indent_generated: "Manager marks the smart meter as installed",
  meter_installed: "Field officer uploads the sealing certificate and marks the meter configured",
  meter_configured: "Manager or admin assigns a technician to activate solar",
  resolved: "Field officer resubmits the file to Discom",
  rejected: "Manager or admin resolves the Discom rejection",
};

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [techs, setTechs] = useState([]);
  const [selectedTech, setSelectedTech] = useState("");
  const [inspTech, setInspTech] = useState("");
  const [uploadType, setUploadType] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [rejectRemark, setRejectRemark] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [editReason, setEditReason] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [noPaper, setNoPaper] = useState(false);
  const [editFileOpen, setEditFileOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const cameraRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/customers/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  useEffect(() => {
    load();
    if (user.role === "manager" || user.role === "super_admin") {
      api.get("/users/technicians").then((r) => setTechs(r.data)).catch(() => {});
    }
  }, [load, user.role]);

  if (!c) return <div className="py-24 flex justify-center"><CircleNotch size={28} className="animate-spin text-slate-400" /></div>;

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doUpload = () =>
    run(async () => {
      const fd = new FormData();
      fd.append("doc_type", role === "technician" ? "site_photo" : uploadType);
      fd.append("file", uploadFile);
      await api.post(`/customers/${id}/documents`, fd);
      setUploadFile(null);
      setUploadType("");
    }, "Document uploaded");

  const download = async (doc) => {
    try {
      const r = await api.get(`/customers/${id}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if ((doc.content_type || "").startsWith("image/")) {
        setLightbox({ url, blob: r.data, doc });
      } else {
        window.open(url, "_blank");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const shareDoc = async () => {
    try {
      const file = new File([lightbox.blob], lightbox.doc.original_filename, { type: lightbox.doc.content_type || "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: lightbox.doc.original_filename });
      } else {
        toast.info("Sharing is not supported on this device. Use Save instead.");
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Could not share");
    }
  };

  const saveDoc = () => {
    const a = document.createElement("a");
    a.href = lightbox.url;
    a.download = lightbox.doc.original_filename;
    a.click();
  };

  const canDeleteDoc = (d) =>
    user.role === "super_admin" ||
    (!["meter_configured", "completed"].includes(c.status) &&
      d.uploaded_by_id === user.id && Date.now() < new Date(d.uploaded_at).getTime() + 4 * 3600 * 1000);

  const deleteDoc = (d) => {
    if (!window.confirm(`Delete "${DOC_LABELS[d.doc_type]}"?`)) return;
    run(() => api.delete(`/customers/${id}/documents/${d.id}`), "Document deleted");
  };

  const openEditFile = () => {
    setEditForm({
      name: c.name, address: c.address, mobile: c.mobile,
      pm_registration_no: c.pm_registration_no, consumer_no: c.consumer_no,
      payment_mode: c.payment_mode, meter_status: c.meter_status,
    });
    setEditFileOpen(true);
  };

  const saveEditFile = () =>
    run(async () => {
      await api.put(`/customers/${id}`, editForm);
      setEditFileOpen(false);
    }, "Customer details updated");

  const deleteFile = () =>
    run(async () => {
      await api.delete(`/customers/${id}`);
      setDeleteOpen(false);
      navigate("/customers");
    }, "File deleted");

  const orderIdx = ORDER.indexOf(c.status);
  const isRejectedFlow = c.status === "rejected" || c.status === "resolved";
  const stepDone = (key) => c.status === "completed" || (orderIdx >= 0 && orderIdx >= ORDER.indexOf(key));
  const activeStepIdx = STEPS.findIndex((s) => !stepDone(s.key));
  const metas = stepDates(c);

  const role = user.role;
  const actions = [];
  if ((role === "operator" || role === "super_admin") && c.status === "created")
    actions.push({ testid: "action-forward-to-manager", label: "Hand over to manager", fn: () => run(() => api.post(`/customers/${id}/actions/forward-to-manager`), "Sent to manager — waiting for confirmation") });
  if ((role === "manager" || role === "super_admin") && c.status === "forwarded_to_manager")
    actions.push({ testid: "action-accept-manager", label: "Confirm file received", fn: () => run(() => api.post(`/customers/${id}/actions/accept-by-manager`), "File receipt confirmed") });
  if ((role === "manager" || role === "super_admin") && c.status === "with_manager")
    actions.push({ testid: "action-forward-to-field", label: "Hand over to field officer", fn: () => run(() => api.post(`/customers/${id}/actions/forward-to-field`), "Sent to field officer — waiting for confirmation") });
  if ((role === "field_officer" || role === "super_admin") && c.status === "forwarded_to_field")
    actions.push({ testid: "action-accept-field", label: "Confirm file received", fn: () => run(() => api.post(`/customers/${id}/actions/accept-by-field`), "File receipt confirmed") });
  if ((role === "field_officer" || role === "super_admin") && ["with_field_officer", "resolved"].includes(c.status))
    actions.push({ testid: "action-submit-discom", label: "Mark as submitted to Discom", fn: () => run(() => api.post(`/customers/${id}/actions/submit-to-discom`), "Marked as submitted to Discom") });
  if ((role === "field_officer" || role === "super_admin") && c.status === "submitted_to_discom")
    actions.push({ testid: "action-generate-indent", label: "Mark indent generated", hint: "Upload the indent copy first", fn: () => run(() => api.post(`/customers/${id}/actions/generate-indent`), "Indent generated") });
  if ((role === "manager" || role === "super_admin") && c.status === "indent_generated")
    actions.push({ testid: "action-meter-installed", label: "Mark smart meter installed", hint: "The field officer uploads the sealing certificate next", fn: () => run(() => api.post(`/customers/${id}/actions/meter-installed`), "Meter installed") });
  if ((role === "field_officer" || role === "super_admin") && c.status === "meter_installed")
    actions.push({ testid: "action-meter-configured", label: "Mark meter configured",
      hint: noPaper ? "Will be marked configured without a document" : "Upload the Discom config screenshot first",
      fn: () => run(() => api.post(`/customers/${id}/actions/meter-configured`, { no_document: noPaper }), "Meter configured") });
  if ((role === "technician" || role === "manager" || role === "super_admin") && c.status === "meter_configured" && (role !== "technician" || c.assigned_technician_id === user.id))
    actions.push({ testid: "action-complete", label: "Activate solar & complete",
      hint: role === "technician" ? "A site photo is required" : undefined,
      fn: () => run(() => api.post(`/customers/${id}/actions/complete`), "Solar activated — job complete") });
  if ((role === "manager" || role === "super_admin") && c.status === "rejected")
    actions.push({ testid: "action-resolve", label: "Resolve & resubmit", fn: () => run(() => api.post(`/customers/${id}/actions/resolve`, {}), "Issue resolved — sent back for indent") });

  const canReject = ["field_officer", "manager", "super_admin"].includes(role) && c.status === "submitted_to_discom";
  const frozen = ["meter_configured", "completed"].includes(c.status);
  const hasSealing = (c.documents || []).some((d) => d.doc_type === "sealing_certificate");
  const techLabel = (role === "super_admin" && c.assigned_technician_id
    ? (techs.find((t) => t.id === c.assigned_technician_id)?.name || c.assigned_technician_name)
    : c.assigned_technician_name);
  const nextStep = NEXT_STEP[c.status];
  const canUpload = role === "super_admin" || (role !== "technician" && !frozen) ||
    (role === "technician" && c.status === "meter_configured" && c.assigned_technician_id === user.id);

  return (
    <div className="space-y-6 stagger-in">
      <div className="rounded-3xl bg-white border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.04)] overflow-hidden" data-testid="customer-header">
        <div className="p-4 sm:p-6 flex flex-wrap sm:flex-nowrap items-start gap-3 sm:gap-4">
          <span className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-brand-50 text-brand-700 font-heading font-extrabold text-xl flex items-center justify-center shrink-0">{(c.name || "?").slice(0, 1).toUpperCase()}</span>
          <div className="min-w-0 flex-1 basis-[calc(100%-4rem)] sm:basis-auto">
            <h1 className="text-xl sm:text-3xl font-heading font-extrabold tracking-tight text-slate-900 leading-tight" data-testid="customer-name">{c.name}</h1>
            <p className="text-sm text-slate-500 mt-0.5 flex items-start gap-1"><MapPin size={14} className="mt-0.5 shrink-0" /> {c.address}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <StatusBadge status={c.status} />
              {c.kw_capacity && <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200"><Lightning size={12} weight="fill" /> {c.kw_capacity} kW</span>}
              <LockTimer lock={c.lock} />
            </div>
          </div>
          <div className="flex gap-1.5 shrink-0 w-full sm:w-auto justify-end -mt-1 sm:mt-0">
            {c.mobile && <a href={`tel:${c.mobile}`} data-testid="call-customer-button" aria-label="Call customer" title="Call customer" className="tap w-10 h-10 rounded-full bg-brand-600 text-white flex items-center justify-center shadow-md shadow-brand-600/25 hover:bg-brand-700"><Phone size={18} weight="fill" /></a>}
            {role === "super_admin" && (
              <>
                <button type="button" data-testid="admin-edit-file-button" onClick={openEditFile} aria-label="Edit file" title="Edit file" className="tap w-10 h-10 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center"><PencilSimple size={18} /></button>
                <button type="button" data-testid="admin-delete-file-button" onClick={() => { setDeleteConfirmText(""); setDeleteOpen(true); }} aria-label="Delete file" title="Delete file" className="tap w-10 h-10 rounded-full border border-slate-200 text-rose-600 hover:bg-rose-50 flex items-center justify-center"><Trash size={18} /></button>
              </>
            )}
          </div>
        </div>
        {((role === "super_admin" && c.lock?.locked) || (role !== "super_admin" && role !== "technician" && c.lock?.locked)) && (
          <div className="px-4 sm:px-6 pb-4 -mt-1 flex flex-wrap gap-2">
          {role === "super_admin" && c.lock?.locked && (
            <Button data-testid="admin-unlock-button" variant="outline" size="sm" onClick={() => run(() => api.post(`/customers/${id}/actions/unlock`), "File unlocked for 4 hours")}>
              <LockKeyOpen size={16} className="mr-1" /> Unlock File
            </Button>
          )}
          {role !== "super_admin" && role !== "technician" && c.lock?.locked && (
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogTrigger asChild>
                <Button data-testid="edit-request-button" variant="outline" size="sm"><PaperPlaneTilt size={16} className="mr-1" /> Edit Request</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Request an edit</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Label>What needs to be corrected?</Label>
                  <div className="flex gap-2 items-start">
                    <Textarea data-testid="edit-request-reason" value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="Describe the correction needed" className="flex-1" />
                    <VoiceTypingButton onText={(t) => setEditReason((v) => (v ? v + " " : "") + t)} />
                  </div>
                  <Button data-testid="edit-request-submit" disabled={!editReason || busy} onClick={() => run(async () => {
                    await api.post("/edit-requests", { customer_id: id, reason: editReason });
                    setEditOpen(false);
                    setEditReason("");
                  }, "Edit request sent to the admin")}>Send request</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          </div>
        )}
      <div className="grid grid-cols-2 md:grid-cols-4 border-t border-slate-100 bg-slate-50/50 text-sm" data-testid="customer-info-grid">
        {[
          ["Mobile", c.mobile], ["Consumer no.", c.consumer_no],
          ...(c.kw_capacity ? [["System size", `${c.kw_capacity} kW`]] : []),
          ["Registration no.", c.pm_registration_no], ["Payment", c.payment_mode],
          ...(c.meter_status !== undefined ? [["Meter", c.meter_status === "smart_pre_installed"
            ? "Smart (pre-installed)"
            : (["meter_installed", "meter_configured", "completed"].includes(c.status)
              ? "Normal → smart installed"
              : "Normal")]] : []),
          ...(c.created_by_name !== undefined ? [["Created by", c.created_by_name]] : []),
          ...(c.assigned_technician_name !== undefined ? [["Engineer", techLabel || "—"]] : []),
          ["Created", formatDate(c.created_at)],
          ...(c.completed_at ? [["Completed", formatDate(c.completed_at)]] : []),
        ].filter(([, v]) => v !== undefined).map(([k, v], i) => (
          <div key={k} className={`px-4 py-3 ${i % 4 !== 0 ? "md:border-l" : ""} ${i % 2 !== 0 ? "max-md:border-l" : ""} ${i >= 2 ? "border-t" : ""} border-slate-100`}>
            <div className="text-[10.5px] font-semibold text-slate-400">{k}</div>
            <div className="text-sm font-semibold text-slate-800 mt-0.5 capitalize break-all">{v}</div>
          </div>
        ))}
      </div>

      </div>

      {c.status === "rejected" && (
        <div data-testid="rejection-banner" className="border border-red-300 bg-red-50 rounded-md p-4">
          <div className="font-bold text-red-700 text-sm">Rejected by Discom</div>
          <div className="text-sm text-red-800 mt-1">Remark: {c.rejection_remark}</div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Timeline */}
        <div className="border border-slate-200/80 rounded-2xl bg-card shadow-[0_1px_3px_rgba(15,23,42,0.04)] p-5">
          <h3 className="font-heading font-bold mb-4 text-slate-900">Progress</h3>
          {(() => { const f = metas[0]?.at; const last = [...metas].reverse().find((m) => m.at); return f && last && dayGap(f, last.at) > 0 ? <p className="text-xs text-slate-500 -mt-2.5 mb-4" data-testid="progress-total-days">{dayGap(f, last.at)} days from file creation to latest step</p> : null; })()}
          <div className="space-y-0">
            {STEPS.map((s, i) => {
              const meta = metas[i];
              const done = stepDone(s.key);
              const active = activeStepIdx === i && !isRejectedFlow;
              return (
                <div key={s.key} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    {done ? <CheckCircle size={22} weight="fill" className="text-brand-500" />
                      : active ? <span className="relative flex w-[22px] h-[22px] items-center justify-center"><span className="absolute inset-0 rounded-full bg-brand-400/40 animate-ping" /><Circle size={22} weight="fill" className="relative text-brand-600" /></span>
                      : isRejectedFlow && activeStepIdx === i ? <XCircle size={22} weight="fill" className="text-red-500" />
                      : <Circle size={22} className="text-border" />}
                    {i < STEPS.length - 1 && <div className={`w-0.5 rounded-full flex-1 min-h-[22px] ${done ? "bg-brand-400" : "bg-slate-200"}`} />}
                  </div>
                  <div className="pb-4 -mt-0.5"><div className={`text-sm ${active ? "font-bold text-slate-900" : done ? "font-semibold text-slate-700" : "font-medium text-slate-400"}`}>{s.label}</div><div className="text-[11px] text-slate-400">{s.sub}</div>{(done || active) && <StepMeta m={meta} active={active} />}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Documents & Actions */}
        <div className="space-y-4">
          <div className="border border-slate-200/80 rounded-2xl bg-card shadow-[0_1px_3px_rgba(15,23,42,0.04)] p-5">
            <h3 className="font-heading font-bold mb-3">Documents</h3>
            {(c.documents || []).length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-documents">No documents yet</p>}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {(c.documents || []).map((d) => (
                <DocThumbnail key={d.id} customerId={id} doc={d} label={DOC_LABELS[d.doc_type]} onOpen={() => download(d)}
                  canDelete={canDeleteDoc(d)} onDelete={() => deleteDoc(d)} />
              ))}
            </div>
            {canUpload && (
              <div className="mt-4 border-t border-border pt-3 space-y-2">
                <Label className="text-xs uppercase tracking-wider font-bold">{role === "technician" ? "Upload site photo (required to complete)" : "Upload document"}</Label>
                <div className="flex flex-wrap gap-2">
                  {role !== "technician" && (
                  <Select value={uploadType} onValueChange={setUploadType}>
                    <SelectTrigger data-testid="doc-type-select" className="h-9 text-xs w-full sm:w-[170px]"><SelectValue placeholder="Document type" /></SelectTrigger>
                    <SelectContent>
                      {[["indent_copy", "Indent copy"], ["sealing_certificate", "Sealing certificate"], ["config_screenshot", "Config screenshot"], ["site_photo", "Site photo"]]
                        .filter(([v]) => !(c.documents || []).some((d) => d.doc_type === v))
                        .filter(([v]) => v !== "sealing_certificate" || ["field_officer", "super_admin"].includes(role))
                        .map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  )}
                  <Input data-testid="doc-file-input" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="flex-1 min-w-[150px] h-9 text-xs" onChange={(e) => setUploadFile(e.target.files[0])} />
                  <Button data-testid="camera-button" type="button" variant="outline" size="icon" aria-label="Take photo" title="Take photo" onClick={() => cameraRef.current?.click()} className="shrink-0 h-9 w-9 rounded-full">
                    <Camera size={17} />
                  </Button>
                  <input ref={cameraRef} data-testid="camera-input" type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={(e) => { if (e.target.files[0]) { setUploadFile(e.target.files[0]); toast.info("Photo captured — tap Upload to save"); } }} />
                  <Button data-testid="upload-doc-button" size="sm" disabled={(role !== "technician" && !uploadType) || !uploadFile || busy} onClick={doUpload} className="shrink-0 h-9 px-4 rounded-full active:scale-95">
                    <FileArrowUp size={15} className="mr-1" /> Upload
                  </Button>
                </div>
                {uploadFile && (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] text-muted-foreground truncate" data-testid="selected-file-name">Selected: {uploadFile.name}</p>
                    <Button data-testid="preview-selected-button" type="button" variant="ghost" size="sm" className="shrink-0 h-7 px-2 text-xs"
                      onClick={() => window.open(URL.createObjectURL(uploadFile), "_blank")}>
                      <Eye size={14} className="mr-1" /> Preview
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border border-slate-200/80 rounded-2xl bg-card shadow-[0_1px_3px_rgba(15,23,42,0.04)] p-5 space-y-3">
            <h3 className="font-heading font-bold text-slate-900">Next action</h3>
            {nextStep && <p className="text-xs text-slate-500 -mt-1.5" data-testid="next-step-inline">{nextStep}</p>}
            {(role === "field_officer" || role === "super_admin") && c.status === "meter_installed" && (
              <label className="flex items-start gap-2 text-xs border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-3 cursor-pointer">
                <input type="checkbox" data-testid="no-paper-checkbox" checked={noPaper} onChange={(e) => setNoPaper(e.target.checked)} className="mt-0.5 accent-amber-600" />
                <span>Configured, but <b>no paper or screenshot</b> received from Discom — mark configured without a document</span>
              </label>
            )}
            {actions.length === 0 && !canReject && <p className="text-sm text-muted-foreground" data-testid="no-actions">Nothing for you to do at this stage</p>}
            <div className="flex flex-wrap gap-2">
              {actions.map((a) => (
                <div key={a.testid}>
                  <Button data-testid={a.testid} className="h-11 px-5 rounded-xl font-semibold shadow-md shadow-brand-600/20" disabled={busy} onClick={a.fn}>{a.label} <ArrowRight size={16} weight="bold" /></Button>
                  {a.hint && <p className="text-[10px] text-muted-foreground mt-1 pl-1">{a.hint}</p>}
                </div>
              ))}
            </div>
            {(role === "manager" || role === "super_admin") && !["meter_installed", "meter_configured", "completed"].includes(c.status) && (
              <div className="border-t border-border pt-3 space-y-2">
                <Label className="text-xs uppercase tracking-wider font-bold">Assign site inspection</Label>
                <div className="flex gap-2">
                  <Select value={inspTech} onValueChange={setInspTech}>
                    <SelectTrigger data-testid="inspection-tech-select" className="h-9 text-xs flex-1"><SelectValue placeholder="Select engineer or technician" /></SelectTrigger>
                    <SelectContent>
                      {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button data-testid="assign-inspection-button" size="sm" className="h-9 px-4 rounded-full text-xs font-semibold active:scale-95" disabled={!inspTech || busy}
                    onClick={() => run(async () => {
                      await api.post("/field-items", {
                        type: "inspection", customer_name: c.name, assigned_to: inspTech,
                        customer_address: c.address || null, customer_mobile: c.mobile || null,
                        text: `${c.address}${c.kw_capacity ? ` · ${c.kw_capacity} kW` : ""}`,
                      });
                      setInspTech("");
                    }, "Inspection assigned. The technician has been notified.")}>
                    Assign
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">The technician will only see name, mobile, address and kW. The full flow runs on the Inspections page.</p>
              </div>
            )}
            {(role === "manager" || role === "super_admin") && c.status === "meter_configured" && !hasSealing && (
              <div className="border-t border-border pt-3">
                <p className="text-xs border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-3" data-testid="sealing-pending-note">
                  The Field Officer must first upload the meter <b>Sealing Certificate</b>. A technician can be assigned after that.
                </p>
              </div>
            )}
            {(role === "manager" || role === "super_admin") && c.status === "meter_configured" && hasSealing && (
              <div className="border-t border-border pt-3 space-y-2">
                <Label className="text-xs uppercase tracking-wider font-bold">Assign technician for solar activation</Label>
                <Select value={selectedTech} onValueChange={setSelectedTech}>
                  <SelectTrigger data-testid="technician-select"><SelectValue placeholder="Select technician" /></SelectTrigger>
                  <SelectContent>
                    {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="assign-technician-button" variant="secondary" size="sm" className="w-full" disabled={!selectedTech || busy}
                  onClick={() => run(() => api.post(`/customers/${id}/actions/assign-technician`, { technician_id: selectedTech }), "Technician assigned and notified")}>
                  Assign & notify
                </Button>
              </div>
            )}
            {canReject && (
              <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="reject-button" variant="destructive" size="sm" className="h-9 px-4 rounded-full text-xs font-semibold active:scale-95">Rejected by Discom</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Mark as rejected by Discom</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Label>Reason for rejection *</Label>
                    <div className="flex gap-2 items-start">
                      <Textarea data-testid="reject-remark-input" value={rejectRemark} onChange={(e) => setRejectRemark(e.target.value)} placeholder="e.g. Document missing, name mismatch..." className="flex-1" />
                      <VoiceTypingButton onText={(t) => setRejectRemark((v) => (v ? v + " " : "") + t)} />
                    </div>
                    <Button data-testid="reject-submit" variant="destructive" disabled={!rejectRemark || busy} onClick={() => run(async () => {
                      await api.post(`/customers/${id}/actions/reject`, { remark: rejectRemark });
                      setRejectOpen(false);
                      setRejectRemark("");
                    }, "Marked as rejected. Admin and manager have been notified.")}>Confirm rejection</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>
      {c.history && (
        <div className="border border-slate-200/80 rounded-2xl bg-card shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
          <button type="button" data-testid="toggle-history-button" onClick={() => setShowHistory((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-3.5 font-heading font-bold text-sm hover:bg-muted transition-colors">
            <span>Activity <span className="text-muted-foreground font-normal">({c.history.length})</span></span>
            <CaretDown size={16} className={`transition-transform ${showHistory ? "rotate-180" : ""}`} />
          </button>
          {showHistory && (
            <div data-testid="history-panel" className="px-5 pb-5 space-y-2 border-t border-border pt-4 max-h-80 overflow-y-auto">
              {nextStep && (
                <div data-testid="next-step-hint" className="text-xs border-l-2 border-amber-500 bg-amber-50 rounded-r-md pl-3 pr-2 py-2 mb-1">
                  <span className="font-bold uppercase tracking-wide text-[10px] text-amber-700">Next step</span>
                  <div className="font-semibold text-amber-900">{nextStep}</div>
                </div>
              )}
              {[...c.history].reverse().map((h, i) => (
                <div key={i} className="text-xs border-l-2 border-primary pl-3 py-1">
                  <span className="font-semibold">{h.label}</span>
                  <span className="text-muted-foreground"> — {h.by_name} ({ROLE_LABELS[h.by_role]})</span>
                  {h.remark && <div className="text-muted-foreground italic">"{h.remark}"</div>}
                  <div className="font-mono text-[10px] text-muted-foreground">{formatDateTime(h.at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {editFileOpen && (
        <Dialog open={editFileOpen} onOpenChange={setEditFileOpen}>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit customer details</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[["name", "Customer name"], ["address", "Address"], ["mobile", "Mobile"],
                ["pm_registration_no", "PM Surya Ghar registration no."], ["consumer_no", "Consumer no."]].map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Input data-testid={`edit-file-${k}`} value={editForm[k] || ""} onChange={(e) => setEditForm((f) => ({ ...f, [k]: e.target.value }))} />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Payment mode</Label>
                  <Select value={editForm.payment_mode} onValueChange={(v) => setEditForm((f) => ({ ...f, payment_mode: v }))}>
                    <SelectTrigger data-testid="edit-file-payment-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Meter type</Label>
                  <Select value={editForm.meter_status} onValueChange={(v) => setEditForm((f) => ({ ...f, meter_status: v }))}>
                    <SelectTrigger data-testid="edit-file-meter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal meter</SelectItem>
                      <SelectItem value="smart_pre_installed">Smart meter already installed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="edit-file-save" className="w-full active:scale-95" disabled={busy} onClick={saveEditFile}>Save changes</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-red-600">Delete this customer file?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="border border-red-300 bg-red-50 rounded-md p-3 text-sm text-red-800">
              You are about to delete the entire file for <b>"{c.name}"</b>. All documents, history and notifications will be permanently removed. This cannot be undone.
            </div>
            <div className="space-y-1.5">
              <Label>Type <b className="font-mono">DELETE</b> below to confirm</Label>
              <Input data-testid="delete-confirm-input" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder="DELETE" className="font-mono" />
            </div>
            <div className="flex gap-2">
              <Button data-testid="delete-cancel-button" variant="outline" className="flex-1" onClick={() => setDeleteOpen(false)}>Cancel</Button>
              <Button data-testid="delete-confirm-button" variant="destructive" className="flex-1 active:scale-95"
                disabled={deleteConfirmText.trim().toUpperCase() !== "DELETE" || busy} onClick={deleteFile}>
                <Trash size={16} className="mr-1" /> Delete permanently
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {lightbox && createPortal(
        <div data-testid="image-lightbox" className="fixed inset-0 z-[100] bg-black">
          <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between p-3 bg-gradient-to-b from-black/80 to-transparent">
            <span className="text-white/80 text-xs font-mono truncate pr-3">{lightbox.doc.original_filename}</span>
            <button type="button" data-testid="lightbox-close-button"
              onClick={() => { URL.revokeObjectURL(lightbox.url); setLightbox(null); }}
              className="bg-white/15 hover:bg-white/30 text-white rounded-full p-2.5 active:scale-90 transition-all shrink-0">
              <X size={22} weight="bold" />
            </button>
          </div>
          <div className="w-full h-full flex items-center justify-center"
            onClick={() => { URL.revokeObjectURL(lightbox.url); setLightbox(null); }}>
            <img src={lightbox.url} alt={lightbox.doc.original_filename} className="w-full h-full object-contain" />
          </div>
          <div className="absolute bottom-0 inset-x-0 z-10 flex justify-center gap-3 p-4 pb-6 bg-gradient-to-t from-black/80 to-transparent">
            <Button data-testid="lightbox-share-button" onClick={shareDoc} className="rounded-full px-6 active:scale-95">
              <ShareNetwork size={18} className="mr-1.5" /> Share
            </Button>
            <Button data-testid="lightbox-save-button" variant="secondary" onClick={saveDoc} className="rounded-full px-6 active:scale-95">
              <DownloadSimple size={18} className="mr-1.5" /> Save
            </Button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
